import { Typography, Card, Icon } from '@equinor/eds-core-react'
import styled from 'styled-components'
import {
    Sensor,
    isSensorEditable,
    isSensorStatusEditable,
    getSensorEditDisabledReason,
} from '@/features/dashboard/models'
import { currentPlantAtom, onboardedEditedAtom, selectedSensorCardAtom } from '@/core/store/globalStore'
import { useAtom, useAtomValue } from 'jotai'
import DetailsButton from '@/components/detailsButton/DetailsButton'
import { InstalledSensorStatus } from '@/api_generated/api'
import {
    assignment_important,
    autorenew,
    check_circle_outlined,
    close_circle_outlined,
    delete_to_trash,
    sync_problem,
} from '@equinor/eds-icons'
import { useGetDevices } from '@/core/hooks-and-services/devicesService'
import { useGetPlants } from '@/core/hooks-and-services/onboardedSensorsService'
import { useConfirmDialog } from '@/hooks'
import { useNavigate } from 'react-router-dom'
import EditButton from '@/components/editButton/EditButton'
import { BatteryLevelIcon, SignalQualityIcon } from '@/components/sensorHealthIcons/SensorHealthIcons'
import { formatSignalQualityDisplay, formatBatteryDisplay } from '@/core/models/sensorHealth'
import { STATUS_COLORS } from '@/core/models/sensorStatusColors'
import SignalDetailsTooltip from './SignalDetailsTooltip'
import type { KeyboardEvent, MouseEvent } from 'react'

interface ISensorCard {
    imageSrc: string
    sensor: Sensor
    toSensorPortfolio?: () => void
    showDetailsButton: boolean
    onEditSensor?: (sensor: Sensor) => void
}

const SensorCard = (props: ISensorCard) => {
    const { imageSrc, sensor, showDetailsButton, onEditSensor } = props

    const [selectedSensorCard, setSelectedSensorCard] = useAtom(selectedSensorCardAtom)
    const currentPlant = useAtomValue(currentPlantAtom)
    const showPlant = !currentPlant?.shortName
    const { data: plants } = useGetPlants()
    const plantName = plants.find((plant) => plant.shortName === sensor.plant)?.description ?? sensor.plant

    const { data: devices } = useGetDevices()
    const filteredDevices = devices.filter((device) => device.portfolio)
    const sensorDevice = filteredDevices.find((d) => d.key === sensor.sensorModel)
    const [onboardedEdited] = useAtom(onboardedEditedAtom)
    const navigate = useNavigate()

    let isSelected = selectedSensorCard?.sensor.sensorDbKey === sensor.sensorDbKey

    const { showConfirm } = useConfirmDialog({
        title: 'Unsaved changes',
        content: 'You have unsaved changes. Are you sure you want to proceed?',
        rejectButtonTitle: 'Discard changes',
        confirmButtonTitle: 'Stay on page',
        onReject: () => {
            setSelectedSensorCard({ sensor: sensor, editOnboarded: true, show: true })
        },
        onConfirm: () => {},
        autoClose: true,
    })

    const { showConfirm: showConfirmDetails } = useConfirmDialog({
        title: 'Unsaved changes',
        content: 'You have unsaved changes. Are you sure you want to proceed?',
        rejectButtonTitle: 'Discard changes',
        confirmButtonTitle: 'Stay on page',
        onReject: () => {
            setSelectedSensorCard({ sensor: sensor, editOnboarded: false, show: true })
        },
        onConfirm: () => {},
        autoClose: true,
    })

    const handleDetailsButton = () => {
        if (onboardedEdited) {
            showConfirmDetails()
            return
        }
        setSelectedSensorCard({ sensor: sensor, editOnboarded: false, show: true })
    }

    const handleEditSensor = () => {
        if (onEditSensor) {
            if (!isSensorEditable(sensor)) {
                return
            }
            const isOnboarded =
                sensor.status === InstalledSensorStatus.Onboarded ||
                sensor.status === InstalledSensorStatus.Active ||
                sensor.status === InstalledSensorStatus.Offline ||
                sensor.status === InstalledSensorStatus.Prepared
            if (isOnboarded) {
                if (onboardedEdited) {
                    showConfirm()
                    return
                }
                setSelectedSensorCard({ sensor: sensor, editOnboarded: true, show: true })
            } else {
                setSelectedSensorCard({ sensor: sensor, editOnboarded: false, show: false })
                onEditSensor(sensor)
            }
        } else {
            setSelectedSensorCard({
                sensor: sensor,
                show: true,
                editOnboarded:
                    sensor.status === InstalledSensorStatus.Onboarded ||
                    sensor.status === InstalledSensorStatus.Active ||
                    sensor.status === InstalledSensorStatus.Offline ||
                    sensor.status === InstalledSensorStatus.Prepared,
            })
            navigate(`/dashboard/${sensor.tag}`)
        }
    }

    const editAvailable = () => isSensorStatusEditable(sensor.status)

    const handleCardClick = (event: MouseEvent<HTMLDivElement>) => {
        const action =
            event.target instanceof Element
                ? event.target.closest('button, a, [role="button"], [tabindex="0"], [data-card-action]')
                : null
        if (action && action !== event.currentTarget) {
            return
        }
        handleDetailsButton()
        navigate(`/dashboard/${editAvailable() ? sensor.sensorDbKey : sensor.tag}`)
    }

    const handleCardKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
        if (event.target !== event.currentTarget || (event.key !== 'Enter' && event.key !== ' ')) return
        event.preventDefault()
        handleDetailsButton()
        navigate(`/dashboard/${editAvailable() ? sensor.sensorDbKey : sensor.tag}`)
    }

    const statusIconConfig = (() => {
        switch (sensor.status) {
            case InstalledSensorStatus.Draft:
                return { icon: assignment_important, color: STATUS_COLORS[InstalledSensorStatus.Draft] }
            case InstalledSensorStatus.Prepared:
                return { icon: check_circle_outlined, color: STATUS_COLORS[InstalledSensorStatus.Prepared] }
            case InstalledSensorStatus.Offline:
                return { icon: close_circle_outlined, color: STATUS_COLORS[InstalledSensorStatus.Offline] }
            case InstalledSensorStatus.Unsuccessful:
                return { icon: sync_problem, color: STATUS_COLORS[InstalledSensorStatus.Unsuccessful] }
            case InstalledSensorStatus.Deleted:
                return { icon: delete_to_trash, color: STATUS_COLORS[InstalledSensorStatus.Deleted] }
            case InstalledSensorStatus.Onboarded:
            case InstalledSensorStatus.Active:
                return { icon: check_circle_outlined, color: STATUS_COLORS[InstalledSensorStatus.Active] }
            case InstalledSensorStatus.Start:
                return { icon: autorenew, color: STATUS_COLORS[InstalledSensorStatus.Start] }
            default:
                return null
        }
    })()

    return (
        <StyledSensorCard
            $isSelectedCard={isSelected}
            onClick={handleCardClick}
            onKeyDown={handleCardKeyDown}
            role="button"
            tabIndex={0}
            aria-label={`Open details for ${sensor.tag || sensor.vendorSerialNumber || 'sensor'}`}
        >
            <CardImageWrapper>
                <img src={imageSrc} />
            </CardImageWrapper>
            <CardDataWrapper $bottom="128px" $left="115px" $subtractRightPadding="16px">
                <TruncatedTypography variant="body_short" color="#565656">
                    {sensor.tag == '' ? sensor.vendorSerialNumber : sensor.tag}
                </TruncatedTypography>
                <TruncatedTypography variant="h5" color="#3D3D3D">
                    {sensorDevice ? sensorDevice.displayName : '-'}
                </TruncatedTypography>
                <StatusBarContainer>
                    {statusIconConfig && (
                        <StatusContainer>
                            <Icon data={statusIconConfig.icon} style={{ color: statusIconConfig.color }} />
                            <StatusValue>{sensor.status ?? '-'}</StatusValue>
                        </StatusContainer>
                    )}
                    <StatusContainer>
                        <BatteryLevelIcon status={sensor.batteryStatus} />
                        <StatusValue>{formatBatteryDisplay(sensor.battery, sensor.batteryEstimated)}</StatusValue>
                    </StatusContainer>
                    <StatusContainer>
                        <SignalDetailsTooltip sensor={sensor}>
                            <SignalQualityIcon quality={sensor.signalQuality} />
                            <StatusValue>{formatSignalQualityDisplay(sensor.signalQuality)}</StatusValue>
                        </SignalDetailsTooltip>
                    </StatusContainer>
                </StatusBarContainer>
            </CardDataWrapper>
            <CardDataWrapper $bottom="55px" $left="16px" $subtractRightPadding="16px">
                <List>
                    <ListItem>
                        <ListEntryLabel color="#6F6F6F">{showPlant ? 'Plant:' : 'Area:'}</ListEntryLabel>
                        <ListEntryData color="#3D3D3D" title={showPlant ? plantName || undefined : undefined}>
                            {showPlant ? plantName || '-' : sensor?.area === null ? '-' : sensor?.area}
                        </ListEntryData>
                    </ListItem>
                    <ListItem>
                        <ListEntryLabel color="#6F6F6F">Superior tag:</ListEntryLabel>
                        <ListEntryData color="#3D3D3D">{sensor.superiorTag}</ListEntryData>
                    </ListItem>
                </List>
            </CardDataWrapper>
            <ButtonGroup>
                <EditButton
                    onClick={handleEditSensor}
                    disabled={!isSensorEditable(sensor)}
                    disabledTooltip={getSensorEditDisabledReason(sensor)}
                />
                {showDetailsButton && (
                    <DetailsButton
                        sensorTag={editAvailable() ? sensor?.sensorDbKey : sensor?.tag}
                        onClick={handleDetailsButton}
                    />
                )}
            </ButtonGroup>
        </StyledSensorCard>
    )
}

export default SensorCard

const StyledSensorCard = styled(Card)<{ $isSelectedCard?: boolean }>`
    width: 414px;
    height: 212px;
    cursor: pointer;
    box-shadow: 0px 3px 4px 0px #0000001f;
    box-shadow: 0px 2px 4px 0px #00000024;
    background-color: ${(props) => (props.$isSelectedCard ? '#e8f8e9' : '#ffffff')};

    &:focus-visible {
        outline: 2px solid var(--eds-color-border-focus);
    }

    @media (max-width: 1024px) {
        width: 100%;
    }
`
const CardImageWrapper = styled.div`
    position: absolute;
    bottom: 117px;
    left: 16px;
    img {
        width: 75px;
        height: 75px;
        object-fit: contain;
    }
`

const CardDataWrapper = styled.div<{
    $bottom: string
    $left: string
    $subtractRightPadding?: string
}>`
    position: absolute;
    bottom: ${({ $bottom }) => $bottom};
    left: ${({ $left }) => $left};
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    width: ${({ $left, $subtractRightPadding }) =>
        $left || $subtractRightPadding ? `calc(100% - ${$left || '0px'} - ${$subtractRightPadding || '0px'})` : '100%'};
    overflow: hidden;
`

const TruncatedTypography = styled(Typography)(({ variant, color }) => ({
    color: color || '#3D3D3D',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    maxWidth: '100%',
}))

const List = styled.ul`
    list-style: none;
    padding-left: 0;
    padding-right: 8px;
    margin: 0;
    width: 100%;
`

const ListItem = styled.li`
    display: flex;
    flex-direction: row;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    align-items: center;
    max-width: 100%;
`

const ListEntryLabel = styled(Typography)`
    display: flex;
    flex: 0 0 100px;
    line-height: 25px;
    text-decoration: none;
`

const ListEntryData = styled(TruncatedTypography)`
    min-width: 0;
    text-decoration: none;
`

const StatusBarContainer = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
`

const StatusContainer = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
`

const StatusValue = styled(Typography)`
    margin-left: 5px;
    color: #3d3d3d;
`

const ButtonGroup = styled.div<{
    $marginLeft?: string
    $marginBottom?: string
    $justifyContent?: string
}>`
    position: absolute;
    left: 16px;
    bottom: 16px;
    width: calc(100% - 32px);
    display: flex;
    justify-content: space-between;
`
